import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import SEO from '../../components/SEO';


const DataProcessingAgreement = () => {
return (
    <div
      id="top"
      className="min-h-screen bg-[#040911] text-white antialiased selection:bg-[#e0eacc] selection:text-[#040911] scroll-smooth"
    >
      <SEO
        title="Data Processing Agreement"
        description="Barrack AI data processing agreement. How we process personal data on behalf of our customers in compliance with applicable data protection laws."
        path="/data-processing-agreement"
      />
      {/* Hero Section */}
      <div className="relative bg-[#040911] overflow-hidden pt-12 md:pt-16 pb-12 md:pb-20">
              <div className="absolute inset-0 bg-grid-pattern opacity-[0.03] z-0 pointer-events-none"></div>
        <div className="absolute top-20 right-[10%] w-96 h-96 bg-[#0a1a2e] opacity-25 rounded-full blur-3xl"></div>
        <div className="absolute -top-10 -left-20 w-72 h-72 bg-[#0a1a2e] opacity-20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-full h-[70%] bg-[#e0eacc] opacity-[0.07] rounded-t-[100%] blur-3xl"></div>
        <div className="absolute -bottom-10 right-10 w-96 h-96 bg-[#e0eacc] opacity-[0.05] rounded-full blur-3xl"></div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10">
          <div className="mb-6 md:mb-8">
            <div className="inline-flex items-center px-4 py-1.5 rounded-md bg-[#0c1723] border-l-4 border-[#e0eacc] text-[#e0eacc] text-sm font-medium">
              Legal Documentation
            </div>
          </div>

          <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 md:mb-6 tracking-tight leading-tight">
            <span className="text-white">Data Processing </span>
            <span className="text-[#e0eacc]">Agreement</span>
          </h1>

          <p className="text-base md:text-lg text-gray-300 mb-3 md:mb-4 max-w-2xl leading-relaxed">
            Effective Date: January 26, 2026
          </p>

          <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-4 md:gap-6 text-sm text-gray-400 mt-6 md:mt-8">
            <div className="flex items-center">
              <div className="w-1 h-10 bg-[#e0eacc] mr-3"></div>
              <div>
   <div className="font-semibold text-white text-base">Data Protection</div>
    <div>Enterprise-Grade Standards</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-16">
        {/* Introduction Section */}
        <section className="bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="prose prose-lg prose-invert max-w-none">
              <p className="text-base md:text-lg">
                This Data Processing Agreement ("DPA") is entered into between Elbourne OPC Private Limited, operating as Barrack AI ("Processor", "we", "us", or "our"), and the Customer ("Controller", "you", or "your") who agrees to the Terms of Service.
              </p>
              <p className="text-sm md:text-base mt-4">
                This DPA sets out the terms and conditions governing the processing of Personal Data by the Processor on behalf of the Customer, in compliance with applicable Data Protection Laws. This DPA is incorporated into and forms part of the Terms of Service.
              </p>
            </div>
          </div>
        </section>

        {/* Table of Contents */}
        <nav className="bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 py-4 md:py-5 border-b border-[#0f1e32] flex justify-between items-center">
            <div className="flex items-center">
              <div className="w-10 md:w-12 h-10 md:h-12 rounded-md bg-[#091423] flex items-center justify-center mr-3 md:mr-4 shadow-inner">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 md:h-6 w-5 md:w-6 text-[#e0eacc]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <div>
                <div className="text-white font-medium text-base md:text-lg">TABLE OF CONTENTS</div>
                <div className="text-xs text-gray-400">Navigate to specific sections</div>
              </div>
            </div>
          </div>

          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
              <div>
                <ol className="list-decimal pl-4 md:pl-6 space-y-2 md:space-y-3">
                  <li>
                    <a href="#section1" className="inline-flex items-center text-[#e0eacc] hover:text-white transition-colors text-sm md:text-base">
                      DEFINITIONS <ChevronRight size={12} className="ml-1 md:w-3.5 md:h-3.5" />
                    </a>
                  </li>
                  <li>
                    <a href="#section2" className="inline-flex items-center text-[#e0eacc] hover:text-white transition-colors text-sm md:text-base">
                      SCOPE AND ROLES <ChevronRight size={12} className="ml-1 md:w-3.5 md:h-3.5" />
                    </a>
                  </li>
                  <li>
                    <a href="#section3" className="inline-flex items-center text-[#e0eacc] hover:text-white transition-colors text-sm md:text-base">
                      PROCESSING OF PERSONAL DATA <ChevronRight size={12} className="ml-1 md:w-3.5 md:h-3.5" />
                    </a>
                  </li>
                  <li>
                    <a href="#section4" className="inline-flex items-center text-[#e0eacc] hover:text-white transition-colors text-sm md:text-base">
                      CUSTOMER OBLIGATIONS <ChevronRight size={12} className="ml-1 md:w-3.5 md:h-3.5" />
                    </a>
                  </li>
                  <li>
                    <a href="#section5" className="inline-flex items-center text-[#e0eacc] hover:text-white transition-colors text-sm md:text-base">
                      PROCESSOR OBLIGATIONS <ChevronRight size={12} className="ml-1 md:w-3.5 md:h-3.5" />
                    </a>
                  </li>
                  <li>
                    <a href="#section6" className="inline-flex items-center text-[#e0eacc] hover:text-white transition-colors text-sm md:text-base">
                      SECURITY MEASURES <ChevronRight size={12} className="ml-1 md:w-3.5 md:h-3.5" />
                    </a>
                  </li>
                  <li>
                    <a href="#section7" className="inline-flex items-center text-[#e0eacc] hover:text-white transition-colors text-sm md:text-base">
                      SUB-PROCESSORS <ChevronRight size={12} className="ml-1 md:w-3.5 md:h-3.5" />
                    </a>
                  </li>
                  <li>
                    <a href="#section8" className="inline-flex items-center text-[#e0eacc] hover:text-white transition-colors text-sm md:text-base">
                      DATA SUBJECT RIGHTS <ChevronRight size={12} className="ml-1 md:w-3.5 md:h-3.5" />
                    </a>
                  </li>
                </ol>
              </div>
              <div>
                <ol className="list-decimal pl-4 md:pl-6 space-y-2 md:space-y-3" start={9}>
                  <li>
                    <a href="#section9" className="inline-flex items-center text-[#e0eacc] hover:text-white transition-colors text-sm md:text-base">
                      PERSONAL DATA BREACH <ChevronRight size={12} className="ml-1 md:w-3.5 md:h-3.5" />
                    </a>
                  </li>
                  <li>
                    <a href="#section10" className="inline-flex items-center text-[#e0eacc] hover:text-white transition-colors text-sm md:text-base">
                      DATA TRANSFERS <ChevronRight size={12} className="ml-1 md:w-3.5 md:h-3.5" />
                    </a>
                  </li>
                  <li>
                    <a href="#section11" className="inline-flex items-center text-[#e0eacc] hover:text-white transition-colors text-sm md:text-base">
                      AUDIT RIGHTS <ChevronRight size={12} className="ml-1 md:w-3.5 md:h-3.5" />
                    </a>
                  </li>
                  <li>
                    <a href="#section12" className="inline-flex items-center text-[#e0eacc] hover:text-white transition-colors text-sm md:text-base">
                      DATA DELETION AND RETURN <ChevronRight size={12} className="ml-1 md:w-3.5 md:h-3.5" />
                    </a>
                  </li>
                  <li>
                    <a href="#section13" className="inline-flex items-center text-[#e0eacc] hover:text-white transition-colors text-sm md:text-base">
                      LIABILITY <ChevronRight size={12} className="ml-1 md:w-3.5 md:h-3.5" />
                    </a>
                  </li>
                  <li>
                    <a href="#section14" className="inline-flex items-center text-[#e0eacc] hover:text-white transition-colors text-sm md:text-base">
                      TERM AND TERMINATION <ChevronRight size={12} className="ml-1 md:w-3.5 md:h-3.5" />
                    </a>
                  </li>
                  <li>
                    <a href="#section15" className="inline-flex items-center text-[#e0eacc] hover:text-white transition-colors text-sm md:text-base">
                      GENERAL PROVISIONS <ChevronRight size={12} className="ml-1 md:w-3.5 md:h-3.5" />
                    </a>
                  </li>
                  <li>
                    <a href="#section16" className="inline-flex items-center text-[#e0eacc] hover:text-white transition-colors text-sm md:text-base">
                      GOVERNING LAW AND JURISDICTION <ChevronRight size={12} className="ml-1 md:w-3.5 md:h-3.5" />
                    </a>
                  </li>
                </ol>
              </div>
            </div>
            <div className="mt-6 pt-4 border-t border-[#0f1e32]">
              <div className="text-sm text-gray-400 mb-2">Schedules</div>
              <div className="flex flex-wrap gap-4">
                <a href="#schedule1" className="inline-flex items-center text-[#e0eacc] hover:text-white transition-colors text-sm md:text-base">
                  Schedule 1: Processing Details <ChevronRight size={12} className="ml-1" />
                </a>
                <a href="#schedule2" className="inline-flex items-center text-[#e0eacc] hover:text-white transition-colors text-sm md:text-base">
                  Schedule 2: Security Measures <ChevronRight size={12} className="ml-1" />
                </a>
              </div>
            </div>
          </div>
        </nav>

        {/* Parties Section */}
        <section className="bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 py-4 md:py-5 border-b border-[#0f1e32]">
            <div className="flex items-center">
              <div className="w-10 md:w-12 h-10 md:h-12 rounded-md bg-[#091423] flex items-center justify-center mr-3 md:mr-4 shadow-inner">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 md:h-6 w-5 md:w-6 text-[#e0eacc]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div>
                <div className="text-white font-medium text-base md:text-lg">PARTIES</div>
                <div className="text-xs text-gray-400">Contracting entities</div>
              </div>
            </div>
          </div>

          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-4 md:p-5 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="font-semibold text-[#e0eacc] mb-2 text-sm md:text-base">Data Processor</p>
                <p className="text-white font-medium text-sm md:text-base">Elbourne OPC Private Limited</p>
                <p className="text-gray-300 text-sm md:text-base">(operating as "Barrack AI")</p>
                <p className="text-gray-400 text-xs md:text-sm mt-2">
                  Registered Address: A9, 2nd Main Road, Anna Nagar, Chengalpattu, Tamil Nadu, India
                </p>
                <p className="text-gray-400 text-xs md:text-sm mt-1">
                  (hereinafter referred to as "Processor", "Barrack AI", "we", "us", or "our")
                </p>
              </div>

              <div className="p-4 md:p-5 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="font-semibold text-[#e0eacc] mb-2 text-sm md:text-base">Data Controller</p>
                <p className="text-white font-medium text-sm md:text-base">The Customer</p>
                <p className="text-gray-300 text-sm md:text-base">who agrees to the Terms of Service and this Data Processing Agreement</p>
                <p className="text-gray-400 text-xs md:text-sm mt-2">
                  (hereinafter referred to as "Controller", "Customer", or "you")
                </p>
              </div>
            </div>
            <p className="text-gray-400 text-sm mt-4">(each a "Party" and together the "Parties")</p>
          </div>
        </section>

        {/* Section 1 - Definitions */}
        <section id="section1" className="scroll-mt-28 bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 py-4 md:py-5 border-b border-[#0f1e32]">
            <div className="flex items-center">
              <div className="w-8 md:w-10 h-8 md:h-10 rounded-md bg-[#091423] flex items-center justify-center mr-3 md:mr-4 shadow-inner">
                <span className="text-lg md:text-xl font-bold text-[#e0eacc]">1</span>
              </div>
              <div>
                <div className="text-white font-medium text-base md:text-lg">DEFINITIONS</div>
              </div>
            </div>
          </div>

          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="prose prose-lg prose-invert max-w-none">
              <p className="text-sm md:text-base mb-6">In this DPA, the following terms shall have the meanings set out below:</p>

              <div className="space-y-4">
                <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                  <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">"Affiliate"</strong> means any entity that directly or indirectly controls, is controlled by, or is under common control with a Party, where "control" means ownership of more than 50% of the voting interests.</p>
                </div>

                <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                  <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">"Customer Data"</strong> means any data, including Personal Data, that the Customer or its Users upload, store, or process using the Services.</p>
                </div>

                <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                  <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">"Data Protection Laws"</strong> means all applicable laws and regulations relating to the processing of Personal Data, including:</p>
                  <ul className="list-disc pl-6 mt-2 space-y-1 text-sm md:text-base text-gray-300">
                    <li>Regulation (EU) 2016/679 (General Data Protection Regulation, "GDPR")</li>
                    <li>The GDPR as implemented under the laws of the United Kingdom ("UK GDPR")</li>
                    <li>The Digital Personal Data Protection Act, 2023 of India ("DPDP Act")</li>
                    <li>The California Consumer Privacy Act ("CCPA") and California Privacy Rights Act ("CPRA")</li>
                    <li>Any other applicable data protection or privacy laws in any relevant jurisdiction</li>
                  </ul>
                </div>

                <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                  <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">"Data Subject"</strong> means an identified or identifiable natural person whose Personal Data is processed.</p>
                </div>

                <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                  <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">"Personal Data"</strong> means any information relating to an identified or identifiable natural person, as defined under applicable Data Protection Laws.</p>
                </div>

                <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                  <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">"Personal Data Breach"</strong> means a breach of security leading to the accidental or unlawful destruction, loss, alteration, unauthorised disclosure of, or access to, Personal Data transmitted, stored, or otherwise processed.</p>
                </div>

                <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                  <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">"Processing"</strong> means any operation or set of operations performed on Personal Data, whether or not by automated means, including collection, recording, organisation, structuring, storage, adaptation, alteration, retrieval, consultation, use, disclosure by transmission, dissemination, alignment, combination, restriction, erasure, or destruction.</p>
                </div>

                <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                  <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">"Processor Personnel"</strong> means the Processor's employees, contractors, agents, and other authorised personnel who process Personal Data under this DPA.</p>
                </div>

                <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                  <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">"Services"</strong> means the cloud computing services provided by the Processor to the Customer as described in the Terms of Service.</p>
                </div>

                <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                  <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">"Standard Contractual Clauses" or "SCCs"</strong> means the standard contractual clauses for the transfer of personal data to third countries adopted by the European Commission pursuant to Decision (EU) 2021/914.</p>
                </div>

                <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                  <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">"Sub-processor"</strong> means any third party engaged by the Processor or its Affiliates to process Personal Data on behalf of the Customer.</p>
                </div>

                <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                  <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">"Supervisory Authority"</strong> means an independent public authority responsible for monitoring the application of Data Protection Laws.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 2 - Scope and Roles */}
        <section id="section2" className="scroll-mt-28 bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 py-4 md:py-5 border-b border-[#0f1e32]">
            <div className="flex items-center">
              <div className="w-8 md:w-10 h-8 md:h-10 rounded-md bg-[#091423] flex items-center justify-center mr-3 md:mr-4 shadow-inner">
                <span className="text-lg md:text-xl font-bold text-[#e0eacc]">2</span>
              </div>
              <div>
                <div className="text-white font-medium text-base md:text-lg">SCOPE AND ROLES</div>
              </div>
            </div>
          </div>

          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="prose prose-lg prose-invert max-w-none space-y-4">
              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">2.1 Roles of the Parties.</strong> For the purposes of this DPA and applicable Data Protection Laws:</p>
                <ul className="list-disc pl-6 mt-2 space-y-1 text-sm md:text-base text-gray-300">
                  <li>The Customer shall be the Controller with respect to Customer Data;</li>
                  <li>The Processor shall be the Processor with respect to Customer Data processed on behalf of the Customer.</li>
                </ul>
              </div>

              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">2.2 Scope of Processing.</strong> This DPA applies to all Processing of Personal Data by the Processor on behalf of the Customer in connection with the provision of the Services.</p>
              </div>

              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">2.3 Customer as Processor.</strong> Where the Customer acts as a processor on behalf of a third-party controller, the Customer warrants that it has obtained all necessary authorisations from such third-party controller to engage the Processor as a sub-processor and to agree to the terms of this DPA.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Section 3 - Processing of Personal Data */}
        <section id="section3" className="scroll-mt-28 bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 py-4 md:py-5 border-b border-[#0f1e32]">
            <div className="flex items-center">
              <div className="w-8 md:w-10 h-8 md:h-10 rounded-md bg-[#091423] flex items-center justify-center mr-3 md:mr-4 shadow-inner">
                <span className="text-lg md:text-xl font-bold text-[#e0eacc]">3</span>
              </div>
              <div>
                <div className="text-white font-medium text-base md:text-lg">PROCESSING OF PERSONAL DATA</div>
              </div>
            </div>
          </div>

          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="prose prose-lg prose-invert max-w-none space-y-4">
              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">3.1 Processing Instructions.</strong> The Processor shall process Personal Data only:</p>
                <ul className="list-disc pl-6 mt-2 space-y-1 text-sm md:text-base text-gray-300">
                  <li>On the documented instructions of the Customer, including as set forth in this DPA and the Terms of Service; or</li>
                  <li>As required by applicable law, in which case the Processor shall, to the extent permitted by law, inform the Customer of such legal requirement before processing.</li>
                </ul>
              </div>

              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">3.2 Details of Processing.</strong> The subject matter, duration, nature, purpose, types of Personal Data, and categories of Data Subjects are described in Schedule 1 to this DPA.</p>
              </div>

              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">3.3 Prohibited Processing.</strong> The Processor shall not:</p>
                <ul className="list-disc pl-6 mt-2 space-y-1 text-sm md:text-base text-gray-300">
                  <li>Process Personal Data for any purpose other than providing the Services;</li>
                  <li>Sell Personal Data;</li>
                  <li>Retain, use, or disclose Personal Data for any commercial purpose other than providing the Services;</li>
                  <li>Process Personal Data outside the scope of the Customer's instructions unless required by law.</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Section 4 - Customer Obligations */}
        <section id="section4" className="scroll-mt-28 bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 py-4 md:py-5 border-b border-[#0f1e32]">
            <div className="flex items-center">
              <div className="w-8 md:w-10 h-8 md:h-10 rounded-md bg-[#091423] flex items-center justify-center mr-3 md:mr-4 shadow-inner">
                <span className="text-lg md:text-xl font-bold text-[#e0eacc]">4</span>
              </div>
              <div>
                <div className="text-white font-medium text-base md:text-lg">CUSTOMER OBLIGATIONS</div>
              </div>
            </div>
          </div>

          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="prose prose-lg prose-invert max-w-none space-y-4">
              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">4.1 Compliance.</strong> The Customer shall:</p>
                <ul className="list-disc pl-6 mt-2 space-y-1 text-sm md:text-base text-gray-300">
                  <li>Comply with all applicable Data Protection Laws in its use of the Services and its processing of Personal Data;</li>
                  <li>Ensure that it has all necessary rights, consents, and legal bases to transfer Personal Data to the Processor for processing;</li>
                  <li>Ensure that its instructions to the Processor comply with applicable Data Protection Laws;</li>
                  <li>Be responsible for the accuracy, quality, and legality of Personal Data provided to the Processor.</li>
                </ul>
              </div>

              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">4.2 Data Subject Information.</strong> The Customer shall ensure that Data Subjects have been provided with all required information regarding the processing of their Personal Data, including information about the Processor's involvement.</p>
              </div>

              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">4.3 Customer Users.</strong> The Customer shall ensure that its Users comply with the terms of this DPA and applicable Data Protection Laws.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Section 5 - Processor Obligations */}
        <section id="section5" className="scroll-mt-28 bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 py-4 md:py-5 border-b border-[#0f1e32]">
            <div className="flex items-center">
              <div className="w-8 md:w-10 h-8 md:h-10 rounded-md bg-[#091423] flex items-center justify-center mr-3 md:mr-4 shadow-inner">
                <span className="text-lg md:text-xl font-bold text-[#e0eacc]">5</span>
              </div>
              <div>
                <div className="text-white font-medium text-base md:text-lg">PROCESSOR OBLIGATIONS</div>
              </div>
            </div>
          </div>

          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="prose prose-lg prose-invert max-w-none space-y-4">
              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">5.1 Confidentiality.</strong> The Processor shall:</p>
                <ul className="list-disc pl-6 mt-2 space-y-1 text-sm md:text-base text-gray-300">
                  <li>Treat all Personal Data as confidential;</li>
                  <li>Ensure that Processor Personnel who process Personal Data are subject to appropriate confidentiality obligations, whether contractual or statutory;</li>
                  <li>Not disclose Personal Data to any third party except as permitted by this DPA or as instructed by the Customer.</li>
                </ul>
              </div>

              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">5.2 Personnel.</strong> The Processor shall take reasonable steps to ensure the reliability of Processor Personnel who have access to Personal Data and ensure that such personnel:</p>
                <ul className="list-disc pl-6 mt-2 space-y-1 text-sm md:text-base text-gray-300">
                  <li>Are informed of the confidential nature of the Personal Data;</li>
                  <li>Have received appropriate training on Data Protection Laws and their responsibilities;</li>
                  <li>Process Personal Data only as necessary to perform their duties.</li>
                </ul>
              </div>

        <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">5.3 Assistance.</strong> The Processor shall, taking into account the nature of the processing and the information available to the Processor, provide reasonable assistance to the Customer in:</p>
                <ul className="list-disc pl-6 mt-2 space-y-1 text-sm md:text-base text-gray-300">
                  <li>Responding to requests from Data Subjects exercising their rights under Data Protection Laws;</li>
                  <li>Ensuring compliance with the Customer's obligations regarding security of processing and notification of Personal Data Breaches.</li>
                </ul>
              </div>

              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">5.4 Data Protection Impact Assessments.</strong> The Processor shall provide reasonable assistance to the Customer with any data protection impact assessments and prior consultations with Supervisory Authorities or other competent authorities which the Customer considers necessary pursuant to Articles 35 and 36 of the GDPR or equivalent provisions under applicable Data Protection Laws. Such assistance shall be limited to the processing of Personal Data under this DPA.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Section 6 - Security Measures */}
        <section id="section6" className="scroll-mt-28 bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 py-4 md:py-5 border-b border-[#0f1e32]">
            <div className="flex items-center">
              <div className="w-8 md:w-10 h-8 md:h-10 rounded-md bg-[#091423] flex items-center justify-center mr-3 md:mr-4 shadow-inner">
                <span className="text-lg md:text-xl font-bold text-[#e0eacc]">6</span>
              </div>
              <div>
                <div className="text-white font-medium text-base md:text-lg">SECURITY MEASURES</div>
              </div>
            </div>
          </div>

          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="prose prose-lg prose-invert max-w-none space-y-4">
              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">6.1 Security Implementation.</strong> The Processor shall implement and maintain appropriate technical and organisational measures to protect Personal Data against:</p>
                <ul className="list-disc pl-6 mt-2 space-y-1 text-sm md:text-base text-gray-300">
                  <li>Accidental or unlawful destruction, loss, or alteration;</li>
                  <li>Unauthorised disclosure or access;</li>
                  <li>Other unlawful forms of processing.</li>
                </ul>
              </div>

              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">6.2 Security Standards.</strong> Such measures shall take into account:</p>
                <ul className="list-disc pl-6 mt-2 space-y-1 text-sm md:text-base text-gray-300">
                  <li>The state of the art and costs of implementation;</li>
                  <li>The nature, scope, context, and purposes of processing;</li>
                  <li>The risks to the rights and freedoms of Data Subjects.</li>
                </ul>
              </div>

              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">6.3 Specific Measures.</strong> Security measures may include, as appropriate:</p>
                <ul className="list-disc pl-6 mt-2 space-y-1 text-sm md:text-base text-gray-300">
                  <li>Encryption of Personal Data in transit;</li>
                  <li>Access controls and authentication mechanisms;</li>
                  <li>Network security measures;</li>
                  <li>Regular testing and assessment of security measures;</li>
                  <li>Personnel security and training;</li>
                  <li>Physical security of facilities.</li>
                </ul>
              </div>

              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">6.4 Technical and Organisational Measures.</strong> The Processor's current technical and organisational security measures are described in Schedule 2 to this DPA. The Processor may update these measures from time to time, provided that such updates do not materially decrease the overall level of security.</p>
              </div>

              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">6.5 Customer Responsibility.</strong> The Customer acknowledges that, pursuant to the Shared Responsibility Model set forth in the Terms of Service, the Customer is solely responsible for:</p>
                <ul className="list-disc pl-6 mt-2 space-y-1 text-sm md:text-base text-gray-300">
                  <li>Operating system configuration, updates, and security patches;</li>
                  <li>Network and firewall configuration within Customer instances;</li>
                  <li>Access management and user permissions within Customer instances;</li>
                  <li>Encryption of data at rest within Customer instances;</li>
                  <li>Application security;</li>
                  <li>Backups and disaster recovery of Customer Data.</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Section 7 - Sub-processors */}
        <section id="section7" className="scroll-mt-28 bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 py-4 md:py-5 border-b border-[#0f1e32]">
            <div className="flex items-center">
              <div className="w-8 md:w-10 h-8 md:h-10 rounded-md bg-[#091423] flex items-center justify-center mr-3 md:mr-4 shadow-inner">
                <span className="text-lg md:text-xl font-bold text-[#e0eacc]">7</span>
              </div>
              <div>
                <div className="text-white font-medium text-base md:text-lg">SUB-PROCESSORS</div>
              </div>
            </div>
          </div>

          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="prose prose-lg prose-invert max-w-none space-y-4">
              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">7.1 Authorisation.</strong> The Customer provides general authorisation for the Processor to engage Sub-processors to process Personal Data, subject to the terms of this Section 7.</p>
              </div>

              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">7.2 Sub-processor List.</strong> A current list of Sub-processors, including their identities and locations, is available upon written request to support@barrack.ai.</p>
              </div>

              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">7.3 Sub-processor Agreements.</strong> The Processor shall:</p>
                <ul className="list-disc pl-6 mt-2 space-y-1 text-sm md:text-base text-gray-300">
                  <li>Conduct appropriate due diligence on Sub-processors prior to engagement;</li>
                  <li>Enter into written agreements with Sub-processors that impose data protection obligations no less protective than those in this DPA;</li>
                  <li>Remain fully liable to the Customer for the performance of Sub-processors' obligations.</li>
                </ul>
              </div>

              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">7.4 New Sub-processors.</strong> The Processor shall provide the Customer with at least thirty (30) days prior written notice before engaging any new Sub-processor, including the name, location, and processing activities of the proposed Sub-processor.</p>
              </div>

              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">7.5 Objection to Sub-processors.</strong> The Customer may object to a new Sub-processor by notifying the Processor in writing within fifteen (15) days of receiving notice. If the Customer objects on reasonable data protection grounds:</p>
                <ul className="list-disc pl-6 mt-2 space-y-1 text-sm md:text-base text-gray-300">
                  <li>The Parties shall work in good faith to find a mutually acceptable solution;</li>
                  <li>If no solution can be found within thirty (30) days, the Customer may terminate the affected Services without penalty by providing written notice to the Processor.</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Section 8 - Data Subject Rights */}
        <section id="section8" className="scroll-mt-28 bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 py-4 md:py-5 border-b border-[#0f1e32]">
            <div className="flex items-center">
              <div className="w-8 md:w-10 h-8 md:h-10 rounded-md bg-[#091423] flex items-center justify-center mr-3 md:mr-4 shadow-inner">
                <span className="text-lg md:text-xl font-bold text-[#e0eacc]">8</span>
              </div>
              <div>
                <div className="text-white font-medium text-base md:text-lg">DATA SUBJECT RIGHTS</div>
              </div>
            </div>
          </div>

          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="prose prose-lg prose-invert max-w-none space-y-4">
              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">8.1 Data Subject Requests.</strong> The Processor shall:</p>
                <ul className="list-disc pl-6 mt-2 space-y-1 text-sm md:text-base text-gray-300">
                  <li>Promptly notify the Customer, and in any event within forty-eight (48) hours, if it receives any request from a Data Subject regarding their Personal Data;</li>
                  <li>Not respond to any such request directly unless authorised by the Customer or required by law;</li>
                  <li>Provide the Customer with reasonable assistance in responding to Data Subject requests.</li>
                </ul>
              </div>

              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">8.2 Assistance.</strong> Taking into account the nature of the processing, the Processor shall assist the Customer by implementing appropriate technical and organisational measures, insofar as this is possible, for the fulfilment of the Customer's obligations to respond to Data Subject requests.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Section 9 - Personal Data Breach */}
        <section id="section9" className="scroll-mt-28 bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 py-4 md:py-5 border-b border-[#0f1e32]">
            <div className="flex items-center">
              <div className="w-8 md:w-10 h-8 md:h-10 rounded-md bg-[#091423] flex items-center justify-center mr-3 md:mr-4 shadow-inner">
                <span className="text-lg md:text-xl font-bold text-[#e0eacc]">9</span>
              </div>
              <div>
                <div className="text-white font-medium text-base md:text-lg">PERSONAL DATA BREACH</div>
              </div>
            </div>
          </div>

          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="prose prose-lg prose-invert max-w-none space-y-4">
              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">9.1 Notification.</strong> The Processor shall notify the Customer without undue delay, and in any event within seventy-two (72) hours, after becoming aware of any Personal Data Breach affecting Personal Data processed under this DPA.</p>
              </div>

              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">9.2 Breach Information.</strong> The notification shall include, to the extent known:</p>
                <ul className="list-disc pl-6 mt-2 space-y-1 text-sm md:text-base text-gray-300">
                  <li>A description of the nature of the Personal Data Breach, including the categories and approximate number of Data Subjects and Personal Data records concerned;</li>
                  <li>The name and contact details of the Processor's point of contact;</li>
                  <li>A description of the likely consequences of the Personal Data Breach;</li>
                  <li>A description of the measures taken or proposed to address the Personal Data Breach, including measures to mitigate its potential adverse effects.</li>
                </ul>
              </div>

              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">9.3 Ongoing Information.</strong> If it is not possible to provide all information at the same time, the Processor shall provide information in phases without undue delay.</p>
              </div>

              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">9.4 Cooperation.</strong> The Processor shall:</p>
                <ul className="list-disc pl-6 mt-2 space-y-1 text-sm md:text-base text-gray-300">
                  <li>Cooperate with the Customer and take reasonable steps to assist in the investigation, mitigation, and remediation of the Personal Data Breach;</li>
                  <li>Document the Personal Data Breach, including its effects and remedial actions taken;</li>
                  <li>Not inform any third party of the Personal Data Breach without the Customer's prior written consent, unless required by law.</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Section 10 - Data Transfers */}
        <section id="section10" className="scroll-mt-28 bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 py-4 md:py-5 border-b border-[#0f1e32]">
            <div className="flex items-center">
              <div className="w-8 md:w-10 h-8 md:h-10 rounded-md bg-[#091423] flex items-center justify-center mr-3 md:mr-4 shadow-inner">
                <span className="text-lg md:text-xl font-bold text-[#e0eacc]">10</span>
              </div>
              <div>
                <div className="text-white font-medium text-base md:text-lg">DATA TRANSFERS</div>
              </div>
            </div>
          </div>

          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="prose prose-lg prose-invert max-w-none space-y-4">
              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">10.1 Processing Locations.</strong> The Processor processes Personal Data in the following locations: United States, Canada, and Finland.</p>
              </div>

              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">10.2 International Transfers.</strong> Where Personal Data is transferred to a country outside the European Economic Area ("EEA"), United Kingdom, or other jurisdiction with data transfer restrictions, the Processor shall ensure that appropriate safeguards are in place, including:</p>
                <ul className="list-disc pl-6 mt-2 space-y-1 text-sm md:text-base text-gray-300">
                  <li>Transfers to countries with an adequacy decision from the relevant authority;</li>
                  <li>Standard Contractual Clauses approved by the European Commission or other relevant authority;</li>
                  <li>Other transfer mechanisms permitted under applicable Data Protection Laws.</li>
                </ul>
              </div>

              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">10.3 Standard Contractual Clauses.</strong> To the extent that the processing of Personal Data involves a transfer from the EEA or United Kingdom to a country not covered by an adequacy decision, the Parties agree that the Standard Contractual Clauses (Module Two: Controller to Processor) shall apply to such transfers and are hereby incorporated by reference.</p>
              </div>

              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">10.4 Additional Measures.</strong> Where required, the Processor shall implement supplementary technical and organisational measures to ensure that transferred Personal Data is afforded a level of protection essentially equivalent to that guaranteed within the EEA or United Kingdom.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Section 11 - Audit Rights */}
        <section id="section11" className="scroll-mt-28 bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 py-4 md:py-5 border-b border-[#0f1e32]">
            <div className="flex items-center">
              <div className="w-8 md:w-10 h-8 md:h-10 rounded-md bg-[#091423] flex items-center justify-center mr-3 md:mr-4 shadow-inner">
                <span className="text-lg md:text-xl font-bold text-[#e0eacc]">11</span>
              </div>
              <div>
                <div className="text-white font-medium text-base md:text-lg">AUDIT RIGHTS</div>
              </div>
            </div>
          </div>

          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="prose prose-lg prose-invert max-w-none space-y-4">
              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">11.1 Information.</strong> The Processor shall make available to the Customer all information reasonably necessary to demonstrate compliance with this DPA and applicable Data Protection Laws.</p>
              </div>

              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">11.2 Audit Right.</strong> The Customer may, upon at least thirty (30) days prior written notice, conduct an audit of the Processor's compliance with this DPA, subject to the following conditions:</p>
                <ul className="list-disc pl-6 mt-2 space-y-1 text-sm md:text-base text-gray-300">
                  <li>Audits may be conducted no more than once per calendar year, unless required by a Supervisory Authority or following a Personal Data Breach;</li>
                  <li>Audits shall be conducted during normal business hours;</li>
                  <li>The Customer shall ensure that auditors are bound by appropriate confidentiality obligations;</li>
                  <li>Audits shall not unreasonably disrupt the Processor's business operations;</li>
                  <li>The Customer shall bear all costs associated with the audit.</li>
                </ul>
              </div>

              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">11.3 Scope.</strong> Audits shall be limited to the Processor's policies, procedures, and systems relevant to the processing of the Customer's Personal Data. The Customer acknowledges that audit rights do not extend to:</p>
                <ul className="list-disc pl-6 mt-2 space-y-1 text-sm md:text-base text-gray-300">
                  <li>Data or systems of other customers;</li>
                  <li>Third-party infrastructure not owned or controlled by the Processor;</li>
                  <li>Proprietary information not related to data protection compliance.</li>
                </ul>
              </div>

              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">11.4 Third-Party Certifications.</strong> As an alternative to an audit, the Customer may accept the Processor's third-party certifications, audit reports (such as SOC 2), or other evidence of compliance, where available.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Section 12 - Data Deletion and Return */}
        <section id="section12" className="scroll-mt-28 bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 py-4 md:py-5 border-b border-[#0f1e32]">
            <div className="flex items-center">
              <div className="w-8 md:w-10 h-8 md:h-10 rounded-md bg-[#091423] flex items-center justify-center mr-3 md:mr-4 shadow-inner">
                <span className="text-lg md:text-xl font-bold text-[#e0eacc]">12</span>
              </div>
              <div>
                <div className="text-white font-medium text-base md:text-lg">DATA DELETION AND RETURN</div>
              </div>
            </div>
          </div>

          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="prose prose-lg prose-invert max-w-none space-y-4">
              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">12.1 Termination.</strong> Upon termination or expiry of the Terms of Service, the Processor shall, at the Customer's written election:</p>
                <ul className="list-disc pl-6 mt-2 space-y-1 text-sm md:text-base text-gray-300">
                  <li>Return all Personal Data to the Customer in a commonly used format; or</li>
                  <li>Delete all Personal Data in the Processor's possession or control.</li>
                </ul>
              </div>

              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">12.2 Timeframe.</strong> Unless otherwise instructed by the Customer, the Processor shall delete all Personal Data within thirty (30) days of termination.</p>
              </div>

              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">12.3 Retention.</strong> The Processor may retain Personal Data to the extent required by applicable law, provided that:</p>
                <ul className="list-disc pl-6 mt-2 space-y-1 text-sm md:text-base text-gray-300">
                  <li>The Processor notifies the Customer of such requirement;</li>
                  <li>The retained Personal Data is protected in accordance with this DPA;</li>
                  <li>The retained Personal Data is not processed for any other purpose.</li>
                </ul>
              </div>

              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">12.4 Certification.</strong> Upon request, the Processor shall provide written certification confirming that it has complied with its obligations under this Section 12.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Section 13 - Liability */}
        <section id="section13" className="scroll-mt-28 bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 py-4 md:py-5 border-b border-[#0f1e32]">
            <div className="flex items-center">
              <div className="w-8 md:w-10 h-8 md:h-10 rounded-md bg-[#091423] flex items-center justify-center mr-3 md:mr-4 shadow-inner">
                <span className="text-lg md:text-xl font-bold text-[#e0eacc]">13</span>
              </div>
              <div>
                <div className="text-white font-medium text-base md:text-lg">LIABILITY</div>
              </div>
            </div>
          </div>

          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="prose prose-lg prose-invert max-w-none space-y-4">
              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">13.1 Limitations.</strong> Each Party's liability under this DPA shall be subject to the limitations and exclusions of liability set forth in the Terms of Service.</p>
              </div>

              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">13.2 Exclusions.</strong> Nothing in this DPA shall limit or exclude either Party's liability for:</p>
                <ul className="list-disc pl-6 mt-2 space-y-1 text-sm md:text-base text-gray-300">
                  <li>Death or personal injury caused by negligence;</li>
                  <li>Fraud or fraudulent misrepresentation;</li>
                  <li>Any other liability that cannot be limited or excluded by applicable law.</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Section 14 - Term and Termination */}
        <section id="section14" className="scroll-mt-28 bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 py-4 md:py-5 border-b border-[#0f1e32]">
            <div className="flex items-center">
              <div className="w-8 md:w-10 h-8 md:h-10 rounded-md bg-[#091423] flex items-center justify-center mr-3 md:mr-4 shadow-inner">
                <span className="text-lg md:text-xl font-bold text-[#e0eacc]">14</span>
              </div>
              <div>
                <div className="text-white font-medium text-base md:text-lg">TERM AND TERMINATION</div>
              </div>
            </div>
          </div>

          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="prose prose-lg prose-invert max-w-none space-y-4">
              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">14.1 Term.</strong> This DPA shall commence on the date the Customer agrees to the Terms of Service and shall continue until the termination or expiry of the Terms of Service.</p>
              </div>

              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">14.2 Survival.</strong> Sections 9, 11, 12, 13, and 15 shall survive termination or expiry of this DPA.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Section 15 - General Provisions */}
        <section id="section15" className="scroll-mt-28 bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 py-4 md:py-5 border-b border-[#0f1e32]">
            <div className="flex items-center">
              <div className="w-8 md:w-10 h-8 md:h-10 rounded-md bg-[#091423] flex items-center justify-center mr-3 md:mr-4 shadow-inner">
                <span className="text-lg md:text-xl font-bold text-[#e0eacc]">15</span>
              </div>
              <div>
                <div className="text-white font-medium text-base md:text-lg">GENERAL PROVISIONS</div>
              </div>
            </div>
          </div>

          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="prose prose-lg prose-invert max-w-none space-y-4">
              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">15.1 Entire Agreement.</strong> This DPA, together with the Terms of Service and Privacy Policy, constitutes the entire agreement between the Parties with respect to the processing of Personal Data.</p>
              </div>

              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">15.2 Amendments.</strong> The Processor may amend this DPA from time to time to reflect changes in Data Protection Laws or processing practices. Material changes shall be notified to the Customer at least thirty (30) days in advance.</p>
              </div>

              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">15.3 Severability.</strong> If any provision of this DPA is found to be invalid or unenforceable, the remaining provisions shall continue in full force and effect.</p>
              </div>

              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">15.4 Assignment.</strong> Neither Party may assign this DPA without the prior written consent of the other Party, except that the Processor may assign this DPA to an Affiliate or in connection with a merger, acquisition, or sale of all or substantially all of its assets.</p>
              </div>

              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">15.5 No Third-Party Rights.</strong> This DPA does not confer any rights on any third party.</p>
              </div>

              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">15.6 Notices.</strong> Notices under this DPA shall be sent:</p>
                <ul className="list-disc pl-6 mt-2 space-y-1 text-sm md:text-base text-gray-300">
                  <li>To the Processor: support@barrack.ai</li>
                  <li>To the Customer: The email address associated with the Customer's account.</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Section 16 - Governing Law and Jurisdiction */}
        <section id="section16" className="scroll-mt-28 bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 py-4 md:py-5 border-b border-[#0f1e32]">
            <div className="flex items-center">
              <div className="w-8 md:w-10 h-8 md:h-10 rounded-md bg-[#091423] flex items-center justify-center mr-3 md:mr-4 shadow-inner">
                <span className="text-lg md:text-xl font-bold text-[#e0eacc]">16</span>
              </div>
              <div>
                <div className="text-white font-medium text-base md:text-lg">GOVERNING LAW AND JURISDICTION</div>
              </div>
            </div>
          </div>

          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="prose prose-lg prose-invert max-w-none space-y-4">
              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">16.1 Governing Law.</strong> This DPA shall be governed by and construed in accordance with the laws of India.</p>
              </div>

              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">16.2 Jurisdiction.</strong> Any disputes arising out of or in connection with this DPA shall be subject to the exclusive jurisdiction of the courts at Chengalpattu, Tamil Nadu, India.</p>
              </div>

              <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-sm md:text-base"><strong className="text-[#e0eacc]">16.3 Regulatory Compliance.</strong> Notwithstanding the above, where Data Protection Laws require the application of different governing law or jurisdiction for certain data protection matters, such requirements shall prevail to the extent necessary for compliance.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Schedule 1 - Processing Details */}
        <section id="schedule1" className="scroll-mt-28 bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 py-4 md:py-5 border-b border-[#0f1e32]">
            <div className="flex items-center">
              <div className="w-10 md:w-12 h-10 md:h-12 rounded-md bg-[#091423] flex items-center justify-center mr-3 md:mr-4 shadow-inner">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 md:h-6 w-5 md:w-6 text-[#e0eacc]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <div className="text-white font-medium text-base md:text-lg">SCHEDULE 1: PROCESSING DETAILS</div>
                <div className="text-xs text-gray-400">Description of the Personal Data Processing</div>
              </div>
            </div>
          </div>

          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="prose prose-lg prose-invert max-w-none space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-white mb-3 flex items-center">
                  <div className="w-1 h-5 bg-[#e0eacc] mr-3"></div>
                  1. Subject Matter of Processing
                </h3>
                <p className="text-sm md:text-base text-gray-300">The processing of Personal Data in connection with the provision of cloud computing services, including GPU virtual machines, storage, and related infrastructure services.</p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-white mb-3 flex items-center">
                  <div className="w-1 h-5 bg-[#e0eacc] mr-3"></div>
                  2. Duration of Processing
                </h3>
                <p className="text-sm md:text-base text-gray-300">The duration of the Terms of Service, plus any period required for data deletion in accordance with this DPA.</p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-white mb-3 flex items-center">
                  <div className="w-1 h-5 bg-[#e0eacc] mr-3"></div>
                  3. Nature and Purpose of Processing
                </h3>
                <p className="text-sm md:text-base text-gray-300 mb-2">The Processor processes Personal Data as necessary to:</p>
                <ul className="list-disc pl-6 space-y-1 text-sm md:text-base text-gray-300">
                  <li>Provide and maintain the Services;</li>
                  <li>Manage Customer accounts;</li>
                  <li>Process payments and billing;</li>
                  <li>Provide customer support;</li>
                  <li>Comply with legal obligations.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-white mb-3 flex items-center">
                  <div className="w-1 h-5 bg-[#e0eacc] mr-3"></div>
                  4. Types of Personal Data
                </h3>
                <p className="text-sm md:text-base text-gray-300 mb-2">The Customer may submit Personal Data to the Services, the extent of which is determined by the Customer, and may include:</p>
                <ul className="list-disc pl-6 space-y-1 text-sm md:text-base text-gray-300">
                  <li>Account information (name, email address, phone number);</li>
                  <li>Billing information (billing address, payment details);</li>
                  <li>Technical information (IP addresses, SSH keys);</li>
                  <li>Any Personal Data uploaded or processed by the Customer using the Services.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-white mb-3 flex items-center">
                  <div className="w-1 h-5 bg-[#e0eacc] mr-3"></div>
                  5. Categories of Data Subjects
                </h3>
                <p className="text-sm md:text-base text-gray-300 mb-2">Data Subjects may include:</p>
                <ul className="list-disc pl-6 space-y-1 text-sm md:text-base text-gray-300">
                  <li>The Customer's employees, contractors, and agents;</li>
                  <li>The Customer's end users and clients;</li>
                  <li>Any individuals whose Personal Data is processed using the Services.</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Schedule 2 - Security Measures */}
        <section id="schedule2" className="scroll-mt-28 bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 py-4 md:py-5 border-b border-[#0f1e32]">
            <div className="flex items-center">
              <div className="w-10 md:w-12 h-10 md:h-12 rounded-md bg-[#091423] flex items-center justify-center mr-3 md:mr-4 shadow-inner">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 md:h-6 w-5 md:w-6 text-[#e0eacc]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div>
                <div className="text-white font-medium text-base md:text-lg">SCHEDULE 2: TECHNICAL AND ORGANISATIONAL MEASURES</div>
                <div className="text-xs text-gray-400">Security measures implemented by the Processor</div>
              </div>
            </div>
          </div>

          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="prose prose-lg prose-invert max-w-none">
              <p className="text-sm md:text-base text-gray-300 mb-6">The Processor implements the following technical and organisational measures to protect Personal Data:</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                  <h4 className="text-[#e0eacc] font-semibold mb-2">1. Access Control</h4>
                  <ul className="list-disc pl-4 space-y-1 text-sm text-gray-300">
                    <li>Role-based access control for Processor Personnel</li>
                    <li>Strong authentication requirements</li>
                    <li>Regular review and revocation of access rights</li>
                    <li>Unique user identification</li>
                  </ul>
                </div>

                <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                  <h4 className="text-[#e0eacc] font-semibold mb-2">2. Transmission Control</h4>
                  <ul className="list-disc pl-4 space-y-1 text-sm text-gray-300">
                    <li>Encryption of data in transit using TLS/SSL</li>
                    <li>Secure communication protocols</li>
                  </ul>
                </div>

                <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                  <h4 className="text-[#e0eacc] font-semibold mb-2">3. Network Security</h4>
                  <ul className="list-disc pl-4 space-y-1 text-sm text-gray-300">
                    <li>Firewalls and intrusion detection systems</li>
                    <li>Network segmentation</li>
                    <li>Regular security monitoring</li>
                  </ul>
                </div>

                <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                  <h4 className="text-[#e0eacc] font-semibold mb-2">4. Availability and Resilience</h4>
                  <ul className="list-disc pl-4 space-y-1 text-sm text-gray-300">
                    <li>Redundant systems and infrastructure</li>
                    <li>Disaster recovery procedures</li>
                    <li>Regular system maintenance</li>
                  </ul>
                </div>

                <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                  <h4 className="text-[#e0eacc] font-semibold mb-2">5. Personnel Security</h4>
                  <ul className="list-disc pl-4 space-y-1 text-sm text-gray-300">
                    <li>Confidentiality obligations for personnel</li>
                    <li>Security awareness training</li>
                    <li>Background checks where appropriate and permitted by law</li>
                  </ul>
                </div>

                <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                  <h4 className="text-[#e0eacc] font-semibold mb-2">6. Incident Management</h4>
                  <ul className="list-disc pl-4 space-y-1 text-sm text-gray-300">
                    <li>Documented incident response procedures</li>
                    <li>Breach detection and notification processes</li>
                    <li>Regular testing of incident response</li>
                  </ul>
                </div>

                <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32] md:col-span-2">
                  <h4 className="text-[#e0eacc] font-semibold mb-2">7. Organisational Measures</h4>
                  <ul className="list-disc pl-4 space-y-1 text-sm text-gray-300">
                    <li>Data protection policies and procedures</li>
                    <li>Regular review and assessment of security measures</li>
                    <li>Vendor management and due diligence</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Agreement Section */}
        <section className="bg-[#060d18] rounded-xl border border-[#0f1e32] overflow-hidden shadow-xl mb-8 md:mb-12 hover:border-[#132b49] transition-all duration-300">
          <div className="px-4 md:px-6 py-4 md:py-5 border-b border-[#0f1e32]">
            <div className="flex items-center">
              <div className="w-10 md:w-12 h-10 md:h-12 rounded-md bg-[#091423] flex items-center justify-center mr-3 md:mr-4 shadow-inner">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 md:h-6 w-5 md:w-6 text-[#e0eacc]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <div className="text-white font-medium text-base md:text-lg">AGREEMENT</div>
                <div className="text-xs text-gray-400">Acceptance of terms</div>
              </div>
            </div>
          </div>

          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="prose prose-lg prose-invert max-w-none">
              <p className="text-sm md:text-base text-gray-300 mb-6">By using the Services, the Customer acknowledges that they have read, understood, and agree to be bound by this Data Processing Agreement.</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                  <p className="font-semibold text-white mb-2">ELBOURNE OPC PRIVATE LIMITED</p>
                  <p className="text-gray-300 text-sm">(operating as Barrack AI)</p>
                  <p className="text-gray-400 text-sm mt-4">Role: Director</p>
                  <p className="text-gray-400 text-sm">Date: January 26, 2026</p>
                </div>

                <div className="p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                  <p className="font-semibold text-white mb-2">CUSTOMER</p>
                  <p className="text-gray-300 text-sm">The Customer is deemed to have agreed to this DPA upon acceptance of the Terms of Service.</p>
                </div>
              </div>

              <div className="mt-8 p-4 bg-[#050a14] rounded-md border border-[#0f1e32]">
                <p className="text-[#e0eacc] font-semibold mb-2">Contact for Data Protection Inquiries</p>
                <p className="text-gray-300 text-sm">Email: support@barrack.ai</p>
              </div>

              <p className="text-gray-400 text-sm mt-6 italic">This Data Processing Agreement was last updated on January 26, 2026.</p>
            </div>
          </div>
        </section>

        {/* Return to Top Button */}
        <div className="flex justify-center my-8 md:my-16">
          <a
            href="#top"
            className="inline-flex items-center gap-2 px-4 md:px-6 py-2 md:py-3 bg-[#091423] hover:bg-[#0a1a2e] text-white rounded-md border border-[#132b49] transition-colors text-sm md:text-base"
            aria-label="Return to top"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 md:h-5 w-4 md:w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
            Return to Top
          </a>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#040911] border-t border-[#0f1e32]">
        <div className="max-w-7xl mx-auto px-4 py-8 md:py-12 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center gap-6">
            <div className="text-xl md:text-2xl font-bold text-white">Barrack™</div>

            <div className="flex space-x-4">
              <a
                href="https://x.com/barrackai"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 md:w-10 h-8 md:h-10 rounded-full bg-[#0f1e32] flex items-center justify-center text-[#e0eacc] hover:bg-[#1c2b49] transition-colors"
                aria-label="Follow on X"
                title="X"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" className="md:w-5 md:h-5" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
                  <path d="M5.026 15c6.038 0 9.341-5.003 9.341-9.334 0-.14 0-.282-.006-.422A6.685 6.685 0 0 0 16 3.542a6.658 6.658 0 0 1-1.889.518 3.301 3.301 0 0 0 1.447-1.817 6.533 6.533 0 0 1-2.087.793A3.286 3.286 0 0 0 7.875 6.03a9.325 9.325 0 0 1-6.767-3.429 3.289 3.289 0 0 0 1.018 4.382A3.323 3.323 0 0 1 .640 6.575v.045a3.288 3.288 0 0 0 2.632 3.218 3.203 3.203 0 0 1-.865.115 3.23 3.23 0 0 1-.614-.057 3.283 3.283 0 0 0 3.067 2.277A6.588 6.588 0 0 1 .78 13.58a6.32 6.32 0 0 1-.78-.045A9.344 9.344 0 0 0 5.026 15z" />
                </svg>
              </a>

              <a
                href="https://www.reddit.com/r/barrack_ai/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 md:w-10 h-8 md:h-10 rounded-full bg-[#0f1e32] flex items-center justify-center text-[#e0eacc] hover:bg-[#1c2b49] transition-colors"
                aria-label="Join us on Reddit"
                title="Reddit"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" className="md:w-5 md:h-5" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
                  <path d="M6.167 8a.831.831 0 0 0-.83.83c0 .459.372.84.83.831a.831.831 0 0 0 0-1.661zm1.843 3.647c.315 0 1.403-.038 1.976-.611a.22.22 0 0 0 .05-.058c.073-.093.123-.203.123-.331 0-.202-.164-.366-.366-.366H8.5c-.202 0-.366.164-.366.366 0 .128.05.238.123.331a.22.22 0 0 0 .05.058c.573.573 1.661.611 1.976.611zM8 0C3.58 0 0 3.58 0 8s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8z" />
                </svg>
              </a>
            </div>

            <div className="flex gap-6">
              <Link to="/privacy-policy" className="text-gray-400 hover:text-white text-sm">Privacy Policy</Link>
              <Link to="/terms-of-service" className="text-gray-400 hover:text-white text-sm">Terms of Service</Link>
              <Link to="/data-processing-agreement" className="text-gray-400 hover:text-white text-sm">DPA</Link>
            </div>

            <div className="text-center">
              <span className="text-gray-400 text-sm">© {new Date().getFullYear()} Barrack™. All rights reserved.</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DataProcessingAgreement;